/**
 * Lenses: ways of looking at this application that a person switches on for
 * themselves.
 *
 * A lens is not a destination and not a theme. It does not take you anywhere -
 * the page you were on stays the page you are on - and it changes no value: it
 * changes what is *drawn on top of* what is already there, and what the
 * navigation offers while it is on. The theme decides how the application is
 * painted; a lens decides how much of it you are being shown.
 *
 * **This module knows no lens by name.** It holds a string, remembers it, and
 * publishes it. Features register themselves at import time with
 * `registerLens`, and one that is removed simply stops registering - a stored
 * name nobody claims resolves to the plain application rather than to a broken
 * one. That is what keeps a feature which owns a lens separable from the
 * application it hangs on: nothing here has to change for one to arrive or go.
 *
 * Three sources, in the order they win:
 *
 * 1. **`?lens=` in the URL**, so a link shows the reader the same picture the
 *    sender was looking at. A lens whose feature is unavailable to the reader
 *    is ignored - they see the plain application, not an error.
 * 2. **The account**, through `cfg.user_preference.extra` on `GET /api/me`, so
 *    the choice follows a person to another machine.
 * 3. **This browser**, written first and read at boot so there is no flash of
 *    the wrong shell before the account answers.
 *
 * With no database the third is the only one, which is the app behaving the way
 * every other stored preference does without one.
 */

import { Atom } from './atom.ts';
import { setLensPreference } from './preferences.ts';

/** What a feature tells the shell about the lens it owns. */
export interface LensInfo {
  /** Stored and carried in links. Lowercase, no spaces. */
  id: string;
  /** How the lens names itself in the control that turns it on. */
  label: string;
  /** The one-line answer to "what does this do to my screen?". */
  description: string;
  /**
   * Whether this account may use it at all. Called on every read, never cached:
   * a flag arrives after the first paint, and a lens whose feature has gone
   * away must stop being available in the same tick.
   */
  isAvailable: () => boolean;
}

const registry = new Map<string, LensInfo>();

const STORAGE_KEY = 'neptune_lens_v1';

/**
 * The lens this browser is looking through, or null for the plain application.
 *
 * Null is a real, common value - most people never turn one on - so it is the
 * default rather than an error state.
 */
export const activeLens = new Atom<string | null>(readStored());

/**
 * Publish the lens for the CSS, which keys off `body[data-lens]`.
 *
 * Guarded, because these modules are imported by unit tests that run in node,
 * where there is no document - and a boot path that throws there would fail
 * tests about something else entirely.
 */
function publish(id: string | null): void {
  if (typeof document === 'undefined' || !document.body) return;
  document.body.dataset.lens = id ?? '';
}

function readStored(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw && /^[a-z][a-z0-9_-]{0,23}$/.test(raw) ? raw : null;
  } catch {
    // Private mode, or storage the browser will not give us. The session still
    // works; the lens is simply not remembered past it.
    return null;
  }
}

function writeStored(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same as above: worth nothing, never worth failing over.
  }
}

/** Register a lens. Called at import time by the feature that owns it. */
export function registerLens(info: LensInfo): void {
  registry.set(info.id, info);
}

/** Every registered lens this account may actually use, in registration order. */
export function availableLenses(): LensInfo[] {
  return [...registry.values()].filter((l) => l.isAvailable());
}

/** One lens by id, whether or not this account may use it. */
export function lensInfo(id: string): LensInfo | undefined {
  return registry.get(id);
}

/**
 * The lens in force: stored, registered, and available to this account.
 *
 * Every consumer asks this rather than reading `activeLens`, because a stored
 * lens is only a name until somebody claims it. A name belonging to a feature
 * this account cannot use resolves to null - that is rule 6 of `docs/plans/argus.md`
 * generalised: a feature you do not have leaves no trace on your screen.
 */
export function currentLens(): string | null {
  const id = activeLens.value;
  if (!id) return null;
  const info = registry.get(id);
  return info && info.isAvailable() ? id : null;
}

/** Whether a particular lens is the one in force. */
export function lensIsOn(id: string): boolean {
  return currentLens() === id;
}

/**
 * Put a lens on, or take it off with `null`.
 *
 * Optimistic, like every other preference: the browser is written and the atom
 * set immediately, and the account hears about it after. A failed write costs a
 * lens that does not follow you to another laptop, which is not worth blocking
 * a click on.
 */
export function setLens(id: string | null): void {
  const next = id && registry.has(id) ? id : null;
  if (next === activeLens.value) return;
  writeStored(next);
  activeLens.set(next);
  publish(next);
  setLensPreference(next);
}

/** Turn the lens on if it is off, off if it is on. */
export function toggleLens(id: string): void {
  setLens(lensIsOn(id) ? null : id);
}

/**
 * Adopt what the account remembers, once, at boot.
 *
 * The value is handed in rather than read out of the preferences atom, the way
 * `applyStoredTheme` takes the theme: this module is the lens, not a second
 * reader of somebody else's store, and the boot path stays a straight line.
 *
 * A browser that already has a lens of its own keeps it. A slow reply must not
 * undo a switch somebody has just flipped - which is the same rule
 * `adoptServerPreferences` keeps for the chart.
 */
export function adoptStoredLens(fromAccount: string | null | undefined): void {
  if (fromAccount === undefined || fromAccount === activeLens.value) return;
  let hasLocalChoice = false;
  try {
    hasLocalChoice = localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    hasLocalChoice = false;
  }
  if (hasLocalChoice) return;
  activeLens.set(fromAccount);
  writeStored(fromAccount);
  publish(fromAccount);
}

/**
 * Read `?lens=` once, at boot, before the first paint.
 *
 * Applied through `setLens`, so an unknown or unavailable name lands on the
 * plain application rather than on a half-drawn one.
 */
export function restoreLensFromUrl(): void {
  if (typeof location === 'undefined') return;
  const wanted = new URLSearchParams(location.search).get('lens');
  if (!wanted) return;
  setLens(wanted);
}

/** Publish the lens in force for the CSS, which keys off `body[data-lens]`. */
export function publishLensAttribute(): void {
  publish(currentLens());
}
